import { micromark } from "micromark";

import { specialChars } from "@smart/itops-utils-basic";

import { PlaceholderData, TAB_PLACEHOLDER } from "./elements";
import {
  buildPlaceholderHtmlExtension,
  placeholderSyntaxExtension,
} from "./placeholder";

type ConvertOptions = {
  template: string;
  data: PlaceholderData;
  prettyDisplayText?: boolean;
};

// keep in sync with the neutral-400 token, not imported to keep the package light
const blockquoteAccentColor = "#7E8EB4";
// single-cell table, not a styled blockquote: Outlook's Word engine drops
// border/padding on blockquote but honours them on td. px not rem, Outlook ignores rem
const blockquoteTableOpen = `<table cellpadding="0" cellspacing="0" border="0" style="margin:4px 0;"><tr><td style="border-left:4px solid ${blockquoteAccentColor};padding-left:16px;">`;
const blockquoteTableClose = "</td></tr></table>";

// each indent level is one tab placeholder, render as non-breaking spaces so email clients keep the indent
const emailIndent = specialChars.nbsp.repeat(4);

export const convertToHtml = (options: ConvertOptions) => {
  const { template, data } = options;
  const placeholderHtmlExtension = buildPlaceholderHtmlExtension(data);

  const html = micromark(template, {
    extensions: [placeholderSyntaxExtension],
    htmlExtensions: [placeholderHtmlExtension],
  });

  return (
    html
      // <u> stored as raw HTML, micromark escapes it so unescape here
      .replace(/&lt;(\/?)u&gt;/g, "<$1u>")
      // blockquote is an accent line, rendered email-safe
      .replace(/<blockquote>/g, blockquoteTableOpen)
      .replace(/<\/blockquote>/g, blockquoteTableClose)
      // indent placeholders become non-breaking spaces so the indent survives
      .replace(new RegExp(TAB_PLACEHOLDER, "g"), emailIndent)
  );
};

const removeCurlyBraces = (str: string) => str.replaceAll(/[{}]/g, "");
const getPlaceholderNameAndCasing = (nameWithCasing: string) => {
  const hasCasing = nameWithCasing.startsWith("_");
  return hasCasing
    ? ({
        casing: "lowercase",
        placeholderName: nameWithCasing.substring(1),
      } as const)
    : { placeholderName: nameWithCasing };
};

export const convertToText = (options: ConvertOptions) => {
  const { template, prettyDisplayText, data } = options;
  let text = template;

  const placeholdersWithLinkText = text.match(/\{\w+\|>[^}]*\}/g);
  for (const placeholder of placeholdersWithLinkText || []) {
    const [placeholderNameWithCasing, linkText] =
      removeCurlyBraces(placeholder).split("|>");
    const value =
      data[
        getPlaceholderNameAndCasing(placeholderNameWithCasing).placeholderName
      ];
    if (value) {
      text = text.replace(
        placeholder,
        prettyDisplayText
          ? `${linkText} - ${value}`
          : `[${linkText}](${value})`,
      );
    }
  }

  const placeholdersWithDisplayText = text.match(/\{\w+\|[^}]*\}/g);
  for (const placeholder of placeholdersWithDisplayText || []) {
    const [placeholderNameWithCasing] =
      removeCurlyBraces(placeholder).split("|");
    const { placeholderName, casing } = getPlaceholderNameAndCasing(
      placeholderNameWithCasing,
    );
    const value = data[placeholderName];
    if (value) {
      text = text.replace(
        placeholder,
        casing === "lowercase" ? value.toLocaleLowerCase() : value,
      );
    }
  }

  const regularPlaceholders = text.match(/\{\w+\}/g);
  for (const placeholder of regularPlaceholders || []) {
    const placeholderNameWithCasing = removeCurlyBraces(placeholder);
    const { placeholderName, casing } = getPlaceholderNameAndCasing(
      placeholderNameWithCasing,
    );
    const value = data[placeholderName];
    if (value) {
      text = text.replace(
        placeholder,
        casing === "lowercase" ? value.toLocaleLowerCase() : value,
      );
    }
  }

  // <u> is stored as raw HTML in markdown — strip the tags for plain text output
  return text.replace(/<\/?u>/g, "");
};

const nbspOnlyParagraphPattern = new RegExp(
  `<p>[\\s${specialChars.nbsp}]*<\\/p>\\n?`,
  "g",
);

const isNbspOnlyParagraph = (paragraph: string) =>
  paragraph.replaceAll(specialChars.nbsp, "").trim() === "";

export const normaliseEmailBodyText = (text: string): string =>
  text
    .split("\n\n")
    .filter((paragraph) => !isNbspOnlyParagraph(paragraph))
    .join("\n\n");

export const normaliseEmailBodyHtml = (html: string): string =>
  html.replace(nbspOnlyParagraphPattern, "");

export const convertToEmailHtml = (options: ConvertOptions) =>
  normaliseEmailBodyHtml(convertToHtml(options));

export const convertToEmailText = (options: ConvertOptions) =>
  normaliseEmailBodyText(convertToText(options));

export const convertSimplifiedHtmlToMarkdown = (html: string): string =>
  html
    .replace(/>\s+</g, "><")
    .replace(/<h1>(.*?)<\/h1>/g, "# $1\n")
    .replace(/<h2>(.*?)<\/h2>/g, "## $1\n")
    .replace(/<h3>(.*?)<\/h3>/g, "### $1\n")
    .replace(/<a\s+href="(.*?)".*?>(.*?)<\/a>/g, "[$2]($1)")
    .replace(/<em>(.*?)<\/em>/g, "*$1*")
    .replace(/<strong>(.*?)<\/strong>/g, "**$1**")
    .replace(/<ul>\s*(<li>.*?<\/li>\s*)+<\/ul>/g, (match) =>
      match.replace(/<\/?ul>/g, "").replace(/<li>(.*?)<\/li>/g, "- $1\n"),
    )
    .replace(/<ol>\s*(<li>.*?<\/li>\s*)+<\/ol>/g, (match) => {
      let count = 0;
      return match
        .replace(/<\/?ol>/g, "")
        .replace(/<li>(.*?)<\/li>/g, (_, item) => {
          count += 1;
          return `${count}. ${item}\n`;
        });
    })
    .replace(/<p>(.*?)<\/p>/g, "$1\n\n")
    .replace(/<\/?[^>]+(>|$)/g, "");

export const removeMarkdown = (
  input: string,
  markdownToKeep: Array<
    "bold" | "italics" | "boldAndItalics" | "links" | "headings" | "blockquotes"
  > = [],
) => {
  let result = input;

  // Remove indent placeholders
  result = result.replace(new RegExp(TAB_PLACEHOLDER, "g"), "");

  // Remove blockquote markers at the start of a line: > text
  // Must run before the heading strip so "> # text" exposes its heading marker
  if (!markdownToKeep.includes("blockquotes")) {
    result = result.replace(/^(\s*)(?:>\s?)+/gm, "$1");
  }

  // Remove heading markers at the start of a line: # text
  if (!markdownToKeep.includes("headings")) {
    result = result.replace(/^(\s*)#{1,6}\s+/gm, "$1");
  }

  // Remove bold and italics combined: **_text_**
  if (!markdownToKeep.includes("boldAndItalics")) {
    result = result.replace(/\*\*_([\s\S]+?)_\*\*/g, "$1");
  }

  // Remove bold formatting: **text**
  if (!markdownToKeep.includes("bold")) {
    result = result.replace(/\*\*([^*]+)\*\*/g, "$1");
  }

  // Remove italics formatting: *text*
  // Using negative lookbehind/lookahead to avoid removing asterisks that are part of bold formatting.
  if (!markdownToKeep.includes("italics")) {
    result = result.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "$1");
  }

  // Remove links: [text](url)
  if (!markdownToKeep.includes("links")) {
    result = result.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1");
  }

  return result;
};
